import { FC, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../../hooks/redux";
import { routes } from "./routes";

/**
 * Gate superadmin-only routes (currently just the Product Utilization
 * dashboard) behind the Cognito `sa` group.
 *
 * Defense in depth: the dashboard's backend endpoints are independently gated
 * on the `sa` group via `get_current_admin`. This guard simply makes the
 * client route unreachable (redirects to the inbox) for non-superadmins so the
 * page/chunk never renders for them.
 */
interface SuperadminRouteGuardProps {
  children: ReactNode;
}

const SUPERADMIN_ONLY_PATHS = [
  routes.productUtilizationDashboard.path,
  routes.npProductivityTimeline.path,
];

export const SuperadminRouteGuard: FC<SuperadminRouteGuardProps> = ({
  children,
}) => {
  const location = useLocation();
  const user = useAppSelector((state) => state.auth?.user);

  const isSuperadminPath = SUPERADMIN_ONLY_PATHS.some((path) =>
    location.pathname.startsWith(path)
  );

  if (isSuperadminPath && !(user?.groups?.includes("sa") ?? false)) {
    return <Navigate to={routes.dashboard.path} replace />;
  }

  return <>{children}</>;
};
